import authSession from "../lib/authSession";

import React, { useState, useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { imageUrl } from "../lib/format";
// import Logo from "../../public/images/logo.png"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faNewspaper,
} from "@fortawesome/free-regular-svg-icons";
import { faCog,
  faSignOutAlt,
  faHouse,
  faSearch,
  faCrown,
  faUser,
  faBriefcase,
  faUserGear,
  faMoneyBillTrendUp,
  faXmark,
  faBars,
  faBook,
  faRoute,
  faStore,
     faFileAlt,
  faClock,
  faKey,
  faReceipt,
  faTags,
  faBuilding,
  faBalanceScale,
} from "@fortawesome/free-solid-svg-icons";
import { useAdminNotifications, useAdminAcademicResources } from "../store/admin";

// Single source of truth for the nav — used by both the mobile drawer
// and the desktop rail, so there's only one list to keep in sync.
const NAV_ITEMS = [
  // Exam tools stay at the very top.
  { title: "Dashboard Overview", icon: faHouse, path: "/admin/dashboard" },
  { title: "Student Management", icon: faUser, path: "/admin/user-management" },
  { title: "Resource Person", icon: faBriefcase, path: "/admin/resource-persons" },
  { title: "Staff Management", icon: faUserGear, path: "/admin/staff-management" },
  // Academic resources sit above the revenue section.
  {
    title: "Academic Resources", icon: faBook,path: "/admin/academic-resources",
    match: ["/admin/resources"],
  },
  { title: "Exam Pins", icon: faKey, path: "/admin/exam-pins" },
  { title: "Exam Purchases", icon: faReceipt, path: "/admin/exam-purchases" },
  { title: "Categories", icon: faTags, path: "/admin/categories" },
  { title: "Departments", icon: faBuilding, path: "/admin/departments" },
  { title: "Learning Paths", icon: faRoute, path: "/admin/learning-paths" },
  { title: "Withdrawal Requests", icon: faMoneyBillTrendUp, path: "/admin/withdrawal-requests" },
  { title: "Marketplace Management", icon: faStore, path: "/admin/marketplace-management" },
  { title: "Revenue Management", icon: faMoneyBillTrendUp, path: "/admin/invoice-management" },
  { title: "Reports", icon: faFileAlt, path: "/admin/reports" },
  { title: "News Management", icon: faNewspaper, path: "/admin/news-management" },
  { title: "Activity Logs", icon: faClock, path: "/admin/activity-logs" },
  { title: "Notifications", icon: faBell, path: "/admin/notifications" },
  { title: "Reconciliation", icon: faBalanceScale, path: "/admin/reconciliation" },
  { title: "Platform Settings", icon: faCog, path: "/admin/settings" },
  // Revenue management at the bottom.
];

const AdminNavBar = ({ title = "Dashboard Overview", subTitle = "", context = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [uimage, setUimage] = useState("");
  const [isloaded, setIsLoaded] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    unreadCount,
    fetchUnreadNotificationCount,
    notifications,
    notificationsLoading,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    notificationMutationLoading,
  } = useAdminNotifications();

  const { pendingModerationCount, fetchPendingModerationCount } = useAdminAcademicResources();

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = React.useRef(null);

  useEffect(() => {
    fetchUnreadNotificationCount();
    fetchPendingModerationCount();
  }, [fetchUnreadNotificationCount, fetchPendingModerationCount]);

  useEffect(() => {
    if (!notifOpen) return;
    fetchNotifications({ page: 1, limit: 8 });
    const onPointerDown = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [notifOpen, fetchNotifications]);

  useEffect(() => {
    // Read the identity from the ADMIN session only. `localStorage.name`/`image`
    // are shared across roles (resource person, staff, learner), so falling back
    // to them can leak another role's name/avatar into the admin header.
    const user = authSession.getUser() || {};
    const firstName = user.first_name || user.firstName || "";
    const lastName = user.last_name || user.lastName || "";
    const fullName =
      user.name || [firstName, lastName].filter(Boolean).join(" ").trim() || "Admin";
    setFirstName(firstName || fullName.split(" ")[0] || "");
    setLastName(lastName || (firstName ? "" : fullName.split(" ").slice(1).join(" ")) || "");
    setUimage(user.image || user.avatar || "");
    setIsLoaded(true);
  }, []);

  const handleLogout = () => {
    authSession.clearSession();
    localStorage.removeItem("isAdmin");
    window.location.href = "/adminlogin";
  };

  // Route-based active state: stable across title changes and nested pages.
  const isActive = (item) =>
    pathname === item.path ||
    pathname.startsWith(`${item.path}/`) ||
    (item.match || []).some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

  // The admin navbar is mounted again when a route changes. Restore each
  // sidebar's saved position before paint so navigation doesn't snap it to top.
  useLayoutEffect(() => {
    document.querySelectorAll("[data-admin-nav]").forEach((container) => {
      const storageKey = `admin-sidebar-scroll-${container.dataset.adminNav}`;
      try {
        const savedPosition = sessionStorage.getItem(storageKey);
        if (savedPosition !== null) container.scrollTop = Number(savedPosition) || 0;
      } catch {
        // Session storage may be unavailable; the sidebar remains scrollable.
      }
    });
  }, []);

  // Keep the active item visible with the smallest necessary sidebar scroll.
  useLayoutEffect(() => {
    document.querySelectorAll("[data-admin-nav]").forEach((container) => {
      const activeItem = container.querySelector('[data-admin-nav-active="true"]');
      if (!activeItem) return;
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
        if (itemRect.top < containerRect.top) {
          container.scrollTop -= containerRect.top - itemRect.top;
        } else {
          container.scrollTop += itemRect.bottom - containerRect.bottom;
        }
      }
    });
  }, [pathname]);

  // Shared nav list markup — rendered once for mobile, once for desktop,
  // so both stay visually and behaviorally identical.
  
  const NavList = (variant) => (
    <ul
      data-admin-nav={variant}
      onScroll={(event) => {
        try {
          sessionStorage.setItem(`admin-sidebar-scroll-${variant}`, String(event.currentTarget.scrollTop));
        } catch {
          // Session storage is optional; scrolling still works normally.
        }
      }}
      className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-1 admin-nav-scroll"
    >
      {NAV_ITEMS.map((item) => {
        const active = isActive(item);
        const icon = active && item.activeIcon ? item.activeIcon : item.icon;
        return (
          <li
            key={item.title}
            data-admin-nav-active={active ? "true" : undefined}
          >
            <button
              type="button"
              onClick={() => navigate(item.path)}
              aria-current={active ? "page" : undefined}
              className={`group flex w-full cursor-pointer items-center gap-3 rounded-xl border-l-4 px-1 py-2.5 text-left text-sm transition-colors ${
                active
                  ? "border-white bg-white/10 font-semibold text-white"
                  : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <FontAwesomeIcon
                icon={icon}
                className={`w-4 shrink-0 ${active ? "text-white " : "text-slate-400 group-hover:text-white/50"}`}
              />
              <span className="flex-1">{item.title}</span>
              {item.title === "Academic Resources" && pendingModerationCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-none text-white">
                  {pendingModerationCount > 99 ? "99+" : pendingModerationCount}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const SidebarBrand = () => (
    <div className="flex flex-col items-start gap-3 border-b border-white/10 px-4 pb-6 pt-8">
      <div className="flex items-center gap-2">
        <img src="/images/logo.png" alt="AI-LMS" className="h-9 w-9 rounded-lg object-contain" />
       <span className="text-2xl text-white">Fillop</span>
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-semibold tracking-wide text-amber-300">
        <FontAwesomeIcon icon={faCrown} className="text-[11px]" />
        Admin
      </span>
    </div>
  );

  const LogoutButton = () => (
    <div className="border-t border-white/10 px-1">
      <button
        onClick={handleLogout}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-red-400"
      >
        <FontAwesomeIcon icon={faSignOutAlt} className="w-4 shrink-0" />
        <span>Logout</span>
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile top bar trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed left-4 top-4 z-40 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16213A] text-white shadow-md md:hidden"
        aria-label="Open menu"
      >
        <FontAwesomeIcon icon={faBars} />
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex w-72 -translate-x-full transform flex-col bg-[#16213A] transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? "translate-x-0" : ""
        }`}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/5 hover:text-white"
          aria-label="Close menu"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <SidebarBrand />
        {NavList("mobile")}
        <LogoutButton />
      </div>

      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-[#16213A] md:flex">
        <SidebarBrand />
        {NavList("desktop")}
        <LogoutButton />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white md:pl-64 ">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-2 ">
          <div className=" flex w-full max-w-sm items-center gap-2 rounded-full bg-slate-100 px-4 py-2 md:ml-0">
            <FontAwesomeIcon icon={faSearch} className="text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => setNotifOpen((value) => !value)}
                className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-[#16213A]"
              >
                <FontAwesomeIcon icon={faBell} />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                    <span className="text-sm font-semibold text-slate-800">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        disabled={notificationMutationLoading}
                        onClick={() => {
                          markAllNotificationsRead();
                          fetchUnreadNotificationCount();
                        }}
                        className="text-xs font-semibold text-[#16213A] hover:underline disabled:opacity-50"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notificationsLoading ? (
                      <div className="px-4 py-6 text-center text-sm text-slate-400">
                        Loading…
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-slate-400">
                        No notifications yet.
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => {
                            if (!notification.read) {
                              markNotificationRead(notification.id);
                              fetchUnreadNotificationCount();
                            }
                            setNotifOpen(false);
                            navigate("/admin/notifications");
                          }}
                          className={`block w-full border-b border-slate-50 px-4 py-3 text-left transition hover:bg-slate-50 ${
                            notification.read ? "" : "bg-[#16213A]/5"
                          }`}
                        >
                          <span className="flex items-start justify-between gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              {notification.title}
                            </span>
                            {!notification.read && (
                              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#16213A]" />
                            )}
                          </span>
                          {notification.body && (
                            <span className="mt-0.5 block truncate text-xs text-slate-500">
                              {notification.body}
                            </span>
                          )}
                          <span className="mt-0.5 block text-[11px] text-slate-400">
                            {notification.createdAt
                              ? new Date(notification.createdAt).toLocaleString()
                              : ""}
                          </span>
                        </button>
                      ))
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setNotifOpen(false);
                      navigate("/admin/notifications");
                    }}
                    className="block w-full border-t border-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-[#16213A] hover:bg-slate-50"
                  >
                    View all notifications
                  </button>
                </div>
              )}
            </div>

            <span className="hidden h-6 w-px bg-slate-200 sm:block" />

            <div className="flex items-center gap-2.5">
              {firstName && lastName ? (
                <span className="hidden text-sm text-slate-600 sm:inline">
                  Hi, <span className="font-semibold text-slate-900">{firstName} {lastName}</span>
                </span>
              ) : null}

              {(uimage || isloaded) && (
                <div className="relative h-9 w-9 shrink-0">
                  <img
                    src={imageUrl(uimage) || "/images/default_profile.png"}
                    alt="Profile"
                    className="h-9 w-9 rounded-full border-2 border-white object-cover shadow"
                  />
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[8px] text-sidebar shadow">
                      <FontAwesomeIcon icon={faCrown} />
                    </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 pb-2 pt-2 md:px-8">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">{title}</h1>
          {subTitle !== "" && <h2 className="text-sm font-medium text-slate-400">/ {subTitle}</h2>}
          {context !== "" && <p className="w-full text-sm text-slate-500">{context}</p>}
        </div>
      </div>
    </>
  );
};

export default AdminNavBar;
